import React from 'react';

/* ------------------------------------------------------------------
 * Icon imports (selective, optimised)
 * ---------------------------------------------------------------- */

import { MapPin, Zap, ShieldCheck } from 'lucide-react';
import {
    BriefcaseIcon,
    StarIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';

/* ------------------------------------------------------------------
 * Types (exported)
 * ---------------------------------------------------------------- */

export type TrustSignalIcon =
    | 'location'
    | 'experience'
    | 'performance'
    | 'reliability'
    | 'reviews'
    | 'turnaround';

export type TrustSignalsVariant =
    | 'clean'
    | 'soft'
    | 'outline';

export type TrustSignalItem = {
    icon: TrustSignalIcon;
    text: string;
};

export type TrustSignalsProps = {
    title?: string;
    items: TrustSignalItem[];
    variant?: TrustSignalsVariant;
    dark?: boolean;
    'data-entity'?: string;
};

/* ------------------------------------------------------------------
 * Icon map
 * ---------------------------------------------------------------- */

const ICON_SIZE_CLASS = 'h-5 w-5';

const iconMap: Record<TrustSignalIcon, React.ReactNode> = {
    location: <MapPin className={ICON_SIZE_CLASS} aria-hidden />,
    experience: <BriefcaseIcon className={ICON_SIZE_CLASS} aria-hidden />,
    performance: <Zap className={ICON_SIZE_CLASS} aria-hidden />,
    reliability: <ShieldCheck className={ICON_SIZE_CLASS} aria-hidden />,
    reviews: <StarIcon className={ICON_SIZE_CLASS} aria-hidden />,
    turnaround: <ClockIcon className={ICON_SIZE_CLASS} aria-hidden />,
};

/* ------------------------------------------------------------------
 * Variant tokens (light + dark)
 * ---------------------------------------------------------------- */

const variantClasses = {
    clean: {
        light: `
            border border-transparent
            bg-transparent
        `,
        dark: `
            border border-transparent
            bg-transparent
        `,
    },
    soft: {
        light: `
            border border-gray-200
            bg-white
            shadow-sm
        `,
        dark: `
            border border-gray-800
            bg-gray-900
        `,
    },
    outline: {
        light: `
            border border-gray-300
            bg-transparent
        `,
        dark: `
            border border-gray-700
            bg-transparent
        `,
    },
};

/* ------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------- */

const TrustSignals = ({
                          title = 'Why Choose Us',
                          items,
                          variant = 'clean',
                          dark = false,
                          'data-entity': dataEntity,
                      }: TrustSignalsProps) => {
    if (!items?.length) return null;

    const titleClass = dark
        ? 'text-white'
        : 'text-gray-900';

    const itemTextClass = dark
        ? 'text-gray-300'
        : 'text-gray-700';

    const iconClass = dark
        ? 'text-gray-400'
        : 'text-gray-700';

    return (
        <section
            data-component="trust-signals"
            data-entity={dataEntity}
            className={`
                rounded-lg
                p-4
                ${dark
                ? variantClasses[variant].dark
                : variantClasses[variant].light}
            `}
        >
            {!!title && (
                <h6
                    className={`mb-3 text-lg font-semibold ${titleClass}`}
                >
                    {title}
                </h6>
            )}

            <ul className="space-y-2">
                {items.map(({ icon, text }, index) => (
                    <li
                        key={`${icon}-${index}`}
                        className={`flex items-start gap-2 text-sm ${itemTextClass}`}
                    >
                        <span
                            className={`mt-[2px] flex-shrink-0 ${iconClass}`}
                        >
                            {iconMap[icon]}
                        </span>
                        <span className="text-sm ">{text}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default TrustSignals;
